// Photo Service - Handle camera and photo uploads

export interface PhotoResult {
  base64: string;
  timestamp: number;
  size: number;
}

// Compress and convert image to base64
async function compressImage(file: File, maxWidth: number = 1200): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with compression
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(base64);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Take photo from camera
export async function takePhoto(): Promise<PhotoResult | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera on mobile

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      try {
        const base64 = await compressImage(file);
        resolve({
          base64,
          timestamp: Date.now(),
          size: base64.length
        });
      } catch (error) {
        console.error('Error processing photo:', error);
        resolve(null);
      }
    };

    input.click();
  });
}

// Upload photo from gallery
export async function uploadPhoto(): Promise<PhotoResult | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      try {
        const base64 = await compressImage(file);
        resolve({
          base64,
          timestamp: Date.now(),
          size: base64.length
        });
      } catch (error) {
        console.error('Error processing photo:', error);
        resolve(null);
      }
    };

    input.click();
  });
}

// Delete photo
export function deletePhoto(photoBase64: string): void {
  // Base64 is stored in database, no file to delete
  console.log('Photo removed from catch');
}

// Get photo size in KB
export function getPhotoSizeKB(base64: string): number {
  return Math.round((base64.length * 3) / 4 / 1024);
}

// Validate photo size (max 5MB)
export function validatePhotoSize(base64: string, maxSizeMB: number = 5): boolean {
  const sizeKB = getPhotoSizeKB(base64);
  return sizeKB <= maxSizeMB * 1024;
}
